import { createContext, useContext } from 'react';
import type { ReactNode } from 'react';

export interface ConfirmOptions {
  title: string;
  message?: ReactNode;
  /** Defaults to "Confirmar". Name the action: "Eliminar", "Cancelar cita". */
  confirmLabel?: string;
  /** Defaults to "Volver" — never "Cancelar", which reads as the action. */
  cancelLabel?: string;
  /** `danger` paints the confirm button for something that cannot be undone. */
  tone?: 'primary' | 'danger';
  /** Also asks for an optional reason, returned with the answer. */
  reason?: { label: string; placeholder?: string };
}

export interface ConfirmResult {
  confirmed: boolean;
  /** Trimmed; empty when none was asked for or none was given. */
  reason: string;
}

export type ConfirmFn = (options: ConfirmOptions) => Promise<ConfirmResult>;

/**
 * Outside a `ConfirmProvider` — a page rendered bare in a unit test — the
 * browser's own dialogs stand in, so a component can always ask.
 */
const browserConfirm: ConfirmFn = (options) => {
  if (options.reason) {
    const answer = window.prompt(`${options.title}\n${options.reason.label}`);
    return Promise.resolve(
      answer === null
        ? { confirmed: false, reason: '' }
        : { confirmed: true, reason: answer.trim() }
    );
  }
  return Promise.resolve({ confirmed: window.confirm(options.title), reason: '' });
};

export const ConfirmContext = createContext<ConfirmFn>(browserConfirm);

/**
 * Asks before doing something that cannot be taken back.
 *
 * ```ts
 * const confirm = useConfirm();
 * const { confirmed } = await confirm({ title: '¿Eliminar este gasto?', tone: 'danger' });
 * if (!confirmed) return;
 * ```
 */
export default function useConfirm(): ConfirmFn {
  return useContext(ConfirmContext);
}
